import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, ChevronDown, ChevronUp, Eye, EyeOff, Lock, MapPin, Save, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_META = [
  { key: "products", label: "Products" },
  { key: "whatwebuild", label: "What We're Building" },
  { key: "story", label: "Story" },
  { key: "platform", label: "Platform" },
  { key: "architecture", label: "Architecture" },
  { key: "app", label: "Customer app prototype" },
  { key: "why", label: "Why VOMARC" },
  { key: "founders", label: "Founders" },
  { key: "stack", label: "Built to scale" },
  { key: "demolinks", label: "Quick links" },
  { key: "contact", label: "Book demo / contact" },
  { key: "investors", label: "Investors" },
  { key: "vault", label: "Pitch deck vault" },
];
const DEFAULT_ORDER = SECTION_META.map((s) => s.key);

export function AdminControls({ settings, update, saveSettings, authHeaders, reload }) {
  const [lockDraft, setLockDraft] = useState({});
  const visible = settings.section_visible || {};
  const locked = settings.section_locked || {};
  const saved = Array.isArray(settings.section_order) ? settings.section_order.filter((k) => DEFAULT_ORDER.includes(k)) : [];
  const order = [...saved];
  DEFAULT_ORDER.forEach((k, i) => { if (!order.includes(k)) order.splice(Math.min(i, order.length), 0, k); });
  const rows = order.map((k) => SECTION_META.find((s) => s.key === k));

  const move = (idx, dir) => {
    const next = [...order];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    update("section_order", next);
  };

  const isOn = (key) => (key === "story" ? visible[key] === true : visible[key] !== false);
  const toggleVisible = (key) => update("section_visible", { ...visible, [key]: !isOn(key) });

  const applyLock = async (key) => {
    const pw = (lockDraft[key] || "").trim();
    if (!pw) { toast.error("Enter a password to lock this section"); return; }
    try {
      await axios.post(`${API}/admin/section-lock`, { key, password: pw }, { headers: authHeaders });
      setLockDraft((c) => ({ ...c, [key]: "" }));
      toast.success("Section locked");
      await reload();
    } catch { toast.error("Could not lock section"); }
  };

  const removeLock = async (key) => {
    try {
      await axios.delete(`${API}/admin/section-lock/${key}`, { headers: authHeaders });
      toast.success("Lock removed");
      await reload();
    } catch { toast.error("Could not remove lock"); }
  };

  return (
    <>
      <section className="admin-panel wide" data-testid="admin-sections-panel">
        <h2>Sections — order, show / hide &amp; password lock</h2>
        <p className="admin-hint">Use the arrows to reorder sections on the public site. Hide a section, or require a password to view it. Save to publish.</p>
        {rows.map((s, idx) => (
          <div key={s.key} className="admin-section-row" data-testid={`admin-section-row-${s.key}`}>
            <div className="admin-order-btns">
              <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} aria-label={`Move ${s.label} up`} data-testid={`admin-section-up-${s.key}`}><ChevronUp size={14} /></button>
              <button type="button" onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} aria-label={`Move ${s.label} down`} data-testid={`admin-section-down-${s.key}`}><ChevronDown size={14} /></button>
            </div>
            <div className="admin-section-name">
              <strong><span className="admin-order-idx" data-testid={`admin-section-index-${s.key}`}>{idx + 1}</span>{s.label}</strong>
              <label className="admin-section-visible">
                <input type="checkbox" checked={isOn(s.key)} onChange={() => toggleVisible(s.key)} data-testid={`admin-section-visible-${s.key}`} />
                <span>{isOn(s.key) ? <><Eye size={13} /> Shown</> : <><EyeOff size={13} /> Hidden</>}</span>
              </label>
            </div>
            <div className="admin-section-lock">
              {locked[s.key] ? (
                <button className="status-btn delete" onClick={() => removeLock(s.key)} data-testid={`admin-section-unlock-${s.key}`}><Unlock size={13} /> Remove password</button>
              ) : (
                <div className="admin-lock-set">
                  <Input type="text" value={lockDraft[s.key] || ""} onChange={(e) => setLockDraft((c) => ({ ...c, [s.key]: e.target.value }))} placeholder="Set password" data-testid={`admin-section-password-${s.key}`} />
                  <button className="status-btn good" onClick={() => applyLock(s.key)} data-testid={`admin-section-lock-${s.key}`}><Lock size={13} /> Lock</button>
                </div>
              )}
            </div>
          </div>
        ))}
        <Button className="primary-cta save-app" onClick={saveSettings} data-testid="admin-save-sections-button"><Save size={17} /> Save order &amp; visibility</Button>
      </section>

      <section className="admin-panel" data-testid="admin-story-panel">
        <h2>Story</h2>
        <p className="admin-hint">Paste a YouTube / Vimeo / any iframe embed, a bare URL, or full HTML. Or just add a link. Toggle it on to show the Story tab.</p>
        <label className="admin-survey-active-toggle">
          <input type="checkbox" checked={isOn("story")} onChange={() => toggleVisible("story")} data-testid="admin-story-enabled-toggle" />
          <span>{isOn("story") ? "Story is ON (visible)" : "Story is OFF (hidden)"}</span>
        </label>
        <label>Title</label>
        <Input value={settings.story_title || ""} onChange={(e) => update("story_title", e.target.value)} data-testid="admin-story-title-input" />
        <label>Short copy</label>
        <Textarea value={settings.story_copy || ""} onChange={(e) => update("story_copy", e.target.value)} rows={2} data-testid="admin-story-copy-input" />
        <label>Embed HTML / iframe / URL</label>
        <Textarea value={settings.story_embed || ""} onChange={(e) => update("story_embed", e.target.value)} rows={4} placeholder='<iframe src="https://www.youtube.com/embed/…"></iframe>' data-testid="admin-story-embed-input" />
        <label>Link (opens in new tab)</label>
        <Input value={settings.story_link || ""} onChange={(e) => update("story_link", e.target.value)} placeholder="https://…" data-testid="admin-story-link-input" />
        <label>Button label</label>
        <Input value={settings.story_button || ""} onChange={(e) => update("story_button", e.target.value)} data-testid="admin-story-button-input" />
        <Button className="primary-cta save-app" onClick={saveSettings} data-testid="admin-save-story-button"><Save size={17} /> Save story</Button>
      </section>

      <section className="admin-panel" data-testid="admin-grid-panel">
        <h2>3D background strength</h2>
        <p className="admin-hint">How visible the spatial grid and point cloud are behind the site.</p>
        <div className="admin-intensity" data-testid="admin-grid-intensity">
          {["subtle", "normal", "bold"].map((level) => (
            <button key={level} type="button" className={(settings.grid_intensity || "normal") === level ? "on" : ""} onClick={() => update("grid_intensity", level)} data-testid={`admin-grid-${level}`}>{level}</button>
          ))}
        </div>
        <Button className="primary-cta save-app" onClick={saveSettings} data-testid="admin-save-grid-button"><Save size={17} /> Save background</Button>
      </section>

      <section className="admin-panel" data-testid="admin-endcta-panel">
        <h2>End-of-page call to action</h2>
        <p className="admin-hint">Appears only when a visitor scrolls to the very bottom.</p>
        <label className="admin-survey-active-toggle">
          <input type="checkbox" checked={settings.end_cta_enabled !== false} onChange={(e) => update("end_cta_enabled", e.target.checked)} data-testid="admin-endcta-enabled-toggle" />
          <span>Enabled</span>
        </label>
        <label>Title</label>
        <Input value={settings.end_cta_title || ""} onChange={(e) => update("end_cta_title", e.target.value)} data-testid="admin-endcta-title-input" />
        <label>Text</label>
        <Textarea value={settings.end_cta_text || ""} onChange={(e) => update("end_cta_text", e.target.value)} data-testid="admin-endcta-text-input" />
        <label>Button label</label>
        <Input value={settings.end_cta_button || ""} onChange={(e) => update("end_cta_button", e.target.value)} data-testid="admin-endcta-button-input" />
        <Button className="primary-cta save-app" onClick={saveSettings} data-testid="admin-save-endcta-button"><Save size={17} /> Save call to action</Button>
      </section>

      <AdminAnalytics authHeaders={authHeaders} />
    </>
  );
}

function AdminAnalytics({ authHeaders }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/analytics`, { headers: authHeaders }); setData(res.data); }
    catch (err) { console.warn("Analytics load failed", err); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="admin-panel" data-testid="admin-analytics-panel">
      <div className="enquiry-header">
        <h2><BarChart3 size={16} /> Visitors</h2>
        <Button className="ghost-cta-dark" onClick={load} data-testid="admin-analytics-refresh"><Eye size={15} /> Refresh</Button>
      </div>
      <p className="admin-hint">Private analytics. Visitors are not shown any of this.</p>
      {!data ? <p className="admin-hint">Loading…</p> : (
        <>
          <div className="analytics-stats">
            <div className="analytics-stat" data-testid="analytics-total"><strong>{data.total}</strong><span>Total visits</span></div>
            <div className="analytics-stat" data-testid="analytics-unique"><strong>{data.unique_visitors}</strong><span>Unique visitors</span></div>
            <div className="analytics-stat" data-testid="analytics-countries"><strong>{data.by_country.length}</strong><span>Locations</span></div>
          </div>
          <div className="analytics-countries" data-testid="analytics-country-list">
            {data.by_country.map((c, i) => (
              <div key={c.country} className="analytics-country-row"><span><MapPin size={12} /> {c.country}</span><em>{c.count}</em></div>
            ))}
            {!data.by_country.length && <p className="admin-hint">No location data yet.</p>}
          </div>
        </>
      )}
    </section>
  );
}
